// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { GetCartsApiParameter } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTablePresentationProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { DataTableCell, DataTableRow } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@tps/App/Components/Notify";
import loadRecentSavedCarts from "@tps/App/Handlers/XNG/RecentSavedCarts";
import { CustomAppState } from "@tps/App/Store/Reducers";
import { getRecentSavedCarts } from "@tps/App/Store/Selectors/RecentSavedCartsSelectors";
import { XngCustomGroup } from "@tps/XDL/constants";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    widgetTitle = "widgetTitle",
    numberOfRecords = "numberOfRecords",
    viewAllLink = "viewAllLink",
    showPONumber = "showPONumber",
    showOrderDate = "showOrderDate",
    showShipToPickUp = "showShipToPickUp",
    showOrderSubTotal = "showOrderSubTotal",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.widgetTitle]: string;
        [fields.numberOfRecords]: number;
        [fields.viewAllLink]: string;
        [fields.showPONumber]: boolean;
        [fields.showOrderDate]: boolean;
        [fields.showShipToPickUp]: boolean;
        [fields.showOrderSubTotal]: boolean;
    };
}
const mapStateToProps = (state: ApplicationState) => {
    const isAuthenticated = state.context.session.isAuthenticated && !state.context.session.isGuest;
    return {
        getSavedOrdersParameter: state.pages.savedOrderList.getCartsApiParameter,
        language: state.context.session.language,
        savedOrderDetailsLink: getPageLinkByPageType(state, "SavedOrderDetailsPage"),
        savedOrderListLink: getPageLinkByPageType(state, "SavedOrderListPage"),
        showPricing: state.context.showPricing,
        savedCartsData: getRecentSavedCarts(state as CustomAppState),
        canViewSavedOrders: isAuthenticated,
    };
};

const mapDispatchToProps = {
    loadRecentSavedCarts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentSavedOrdersStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: InjectableCss;
    headingWrapper?: InjectableCss;
    widgetTitle?: TypographyPresentationProps;
    viewAllLink?: LinkPresentationProps;
    savedCartsContainer?: InjectableCss;
    noResults?: InjectableCss;
    dataTable?: DataTablePresentationProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeader?: DataTableHeaderPresentationProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCell?: DataTableCellProps;
}

export const recentSavedOrdersStyles: RecentSavedOrdersStyles = {};

const styles = recentSavedOrdersStyles;

const RecentSavedOrders: React.FC<Props> = props => {
    const {
        getSavedOrdersParameter,
        language,
        savedOrderDetailsLink,
        showPricing,
        savedOrderListLink,
        canViewSavedOrders,
        savedCartsData,
        loadRecentSavedCarts,
    } = props;
    const {
        fields: {
            widgetTitle,
            numberOfRecords,
            showPONumber,
            showOrderDate,
            showShipToPickUp,
            showOrderSubTotal,
            viewAllLink,
        },
    } = props;
    const savedCartsParmas: GetCartsApiParameter = { ...getSavedOrdersParameter };
    React.useEffect(() => {
        if (numberOfRecords) {
            savedCartsParmas.pageSize = numberOfRecords;
            savedCartsParmas.page = 1;
        }
        if (!savedCartsData.value.carts && !savedCartsData.loading) {
            loadRecentSavedCarts({ apiParameter: savedCartsParmas });
        }

        // if this is undefined it means someone changed the filters and we haven't loaded the new collection yet
    }, []);

    const savedOrderDetailsUrl = savedOrderDetailsLink ? savedOrderDetailsLink.url : undefined;
    const savedOrderListLinkUrl = savedOrderListLink ? savedOrderListLink?.url : undefined;

    if (!canViewSavedOrders) {
        return null;
    }
    if (!savedCartsData.value.carts && savedCartsData.loading) {
        return (
            <>
                <StyledWrapper {...styles.headingWrapper}>
                    <Typography {...styles.widgetTitle}>{widgetTitle}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner}></LoadingSpinner>
                </StyledWrapper>
            </>
        );
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="recentSavedCartsWidget">
            <StyledWrapper {...styles.headingWrapper}>
                <Typography {...styles.widgetTitle}>{widgetTitle}</Typography>
                <Link {...styles.viewAllLink} href={savedOrderListLinkUrl}>
                    {viewAllLink}
                </Link>
            </StyledWrapper>
            <StyledWrapper {...styles.savedCartsContainer}>
                {savedCartsData.value?.carts?.length === 0 && (
                    <Notify type="info" {...styles.noResults}>
                        {translate("No saved carts found")}
                    </Notify>
                )}
                {savedCartsData.value?.carts?.length !== 0 && (
                    <DataTable {...styles.dataTable}>
                        <DataTableHead {...styles.dataTableHead}>
                            {showPONumber && (
                                <DataTableHeader tight title={translate("PO #")} {...styles.dataTableHeader}>
                                    {translate("PO #")}
                                </DataTableHeader>
                            )}
                            {showOrderDate && (
                                <DataTableHeader tight title={translate("Date")} {...styles.dataTableHeader}>
                                    {translate("Date")}
                                </DataTableHeader>
                            )}
                            {showShipToPickUp && (
                                <DataTableHeader tight title={translate("Ship To/Pick Up")} {...styles.dataTableHeader}>
                                    {translate("Ship To/Pick Up")}
                                </DataTableHeader>
                            )}
                            {showOrderSubTotal && (
                                <DataTableHeader tight title={translate("Order Subtotal")} {...styles.dataTableHeader}>
                                    {translate("Order Subtotal")}
                                </DataTableHeader>
                            )}
                        </DataTableHead>
                        <DataTableBody {...styles.dataTableBody}>
                            {savedCartsData.value?.carts?.map(order => {
                                const savedOrder = {
                                    id: order.id,
                                    poNumber: order.poNumber,
                                    date: order.orderDate
                                        ? getLocalizedDateTime({
                                              dateTime: new Date(order.orderDate),
                                              language,
                                          })
                                        : "",
                                    shipToLabel: order.shipToLabel,
                                    subtotal: order.orderSubTotalDisplay,
                                };
                                return (
                                    <DataTableRow
                                        data-test-selector="savedCartList_orderLine"
                                        data-test-key={savedOrder.id}
                                        key={savedOrder.id}
                                        {...styles.dataTableRow}
                                    >
                                        {showPONumber && (
                                            <DataTableCell {...styles.dataTableCell}>
                                                {savedOrder.poNumber}
                                            </DataTableCell>
                                        )}
                                        {showOrderDate && (
                                            <DataTableCell {...styles.dataTableCell}>
                                                <Link href={`${savedOrderDetailsUrl}?cartId=${savedOrder.id}`}>
                                                    {savedOrder.date}
                                                </Link>
                                            </DataTableCell>
                                        )}
                                        {showShipToPickUp && (
                                            <DataTableCell
                                                {...styles.dataTableCell}
                                                data-test-selector="savedOrderLine_shipTo"
                                            >
                                                {savedOrder.shipToLabel}
                                            </DataTableCell>
                                        )}
                                        {showPricing && showOrderSubTotal && (
                                            <DataTableCell {...styles.dataTableCell}>
                                                {savedOrder.subtotal}
                                            </DataTableCell>
                                        )}
                                    </DataTableRow>
                                );
                            })}
                        </DataTableBody>
                    </DataTable>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentSavedOrders),
    definition: {
        group: XngCustomGroup,
        icon: "list-ul",
        displayName: "Recent Saved Carts",
        fieldDefinitions: [
            {
                name: fields.widgetTitle,
                fieldType: "Contextual",
                displayName: translate("Title"),
                editorTemplate: "TextField",
                defaultValue: "Recent Saved Carts",
            },
            {
                name: fields.viewAllLink,
                fieldType: "Contextual",
                displayName: translate("View All link text"),
                editorTemplate: "TextField",
                defaultValue: "View All Saved Carts",
            },
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Quotes Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.showPONumber,
                fieldType: "General",
                displayName: translate("Show PO #"),
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
            {
                name: fields.showOrderDate,
                fieldType: "General",
                displayName: translate("Show Order Date"),
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
            {
                name: fields.showOrderSubTotal,
                fieldType: "General",
                displayName: translate("Show Subtotal"),
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
            {
                name: fields.showShipToPickUp,
                fieldType: "General",
                displayName: translate("Show Ship To/Pick Up"),
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
        ],
    },
};

export default widgetModule;
